
import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiCall } from '@/lib/api/api-helpers'
import type {
  Canvas,
  CanvasCreateInput,
  CanvasUpdateInput,
  CanvasFilters,
  CanvasListMeta,
  UseCanvasReturn,
} from '@/lib/api/canvas-types'

/** Page size used when a caller does not ask for one. Matches the API default. */
const DEFAULT_LIMIT = 20

/** What the list endpoint answers with. */
interface CanvasListResponse {
  success: boolean
  data?: Canvas[]
  meta?: Partial<CanvasListMeta>
  error?: string
}

export function useCanvas(filters?: CanvasFilters): UseCanvasReturn {
  const [canvases, setCanvases] = useState<Canvas[]>([])
  const [trashedCanvases, setTrashedCanvases] = useState<Canvas[]>([])
  const [meta, setMeta] = useState<CanvasListMeta | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isTrashLoading, setIsTrashLoading] = useState(false)
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated, user } = useAuth()
  const abortRef = useRef<AbortController | null>(null)
  const trashAbortRef = useRef<AbortController | null>(null)
  const loadMoreAbortRef = useRef<AbortController | null>(null)
  // Single-flight guard for `loadMore`. The `isLoadingMore` state cannot do this job: the
  // scroll sentinel and the Load more button can both call the same closure within one tick,
  // before React has re-rendered, and two calls would advance `loadedPagesRef` twice — skipping
  // a page of sessions that nothing would ever request again.
  const isLoadingMoreRef = useRef(false)

  // Extract user ID for stable dependency (optimization: avoid callback recreation on user object mutation)
  const userId = user?.id

  // Extract primitive filter values to prevent infinite fetch loops from inline object references
  const filterProjectId = filters?.projectId
  const filterDraftsOnly = filters?.draftsOnly
  const filterIsRecent = filters?.isRecent
  const filterIsPublic = filters?.isPublic
  const filterIncludeDeleted = filters?.includeDeleted
  const filterPage = filters?.page
  const filterLimit = filters?.limit

  const firstPage = filterPage ?? 1
  const pageSize = filterLimit ?? DEFAULT_LIMIT

  // How many consecutive pages are currently in `canvases`. Growing this is what "load more" does;
  // a refetch reloads exactly these pages so a mutation never collapses the list back to page one.
  const loadedPagesRef = useRef(1)

  // Wrapper around shared apiCall that passes isAuthenticated from hook context
  const callApi = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    return apiCall(endpoint, isAuthenticated, options)
  }, [isAuthenticated])

  // Build the query string for one page. Filtering and ordering happen on the server, so the page
  // the user sees is a window onto the whole list, not onto whatever the first request returned.
  const buildQueryString = useCallback((page: number) => {
    const params = new URLSearchParams()

    if (filterProjectId) params.append('projectId', filterProjectId)
    if (filterDraftsOnly) params.append('draftsOnly', 'true')
    if (filterIsRecent) params.append('isRecent', 'true')
    if (filterIsPublic) params.append('isPublic', 'true')
    if (filterIncludeDeleted) params.append('includeDeleted', 'true')
    params.append('page', page.toString())
    params.append('limit', pageSize.toString())

    return `?${params.toString()}`
  }, [filterProjectId, filterDraftsOnly, filterIsRecent, filterIsPublic, filterIncludeDeleted, pageSize])

  const fetchPage = useCallback(async (page: number, signal: AbortSignal): Promise<{ canvases: Canvas[]; meta: CanvasListMeta }> => {
    const response = await callApi(`/api/canvas${buildQueryString(page)}`, { signal }) as CanvasListResponse

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch sessions')
    }

    const total = response.meta?.total ?? response.data.length
    return {
      canvases: response.data,
      meta: {
        total,
        page,
        limit: pageSize,
        totalPages: response.meta?.totalPages ?? Math.max(1, Math.ceil(total / pageSize)),
        hasMore: response.meta?.hasMore ?? page * pageSize < total,
      },
    }
  }, [callApi, buildQueryString, pageSize])

  // Fetch (or refetch) every page currently loaded
  const fetchCanvases = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setCanvases([])
      setMeta(null)
      setIsLoading(false)
      return
    }

    try {
      // Delay showing loading skeleton to avoid flicker on fast loads
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current)
      loadingTimerRef.current = setTimeout(() => setIsLoading(true), 150)
      setError(null)

      // Abort any previous in-flight fetch, including a page append: this reload covers every
      // page already loaded, so an older page landing after it would re-add rows it just dropped.
      abortRef.current?.abort()
      loadMoreAbortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      const pages = Array.from(
        { length: loadedPagesRef.current },
        (_, index) => firstPage + index,
      )
      const results = await Promise.all(pages.map(page => fetchPage(page, controller.signal)))

      setCanvases(results.flatMap(result => result.canvases))
      // The last page's meta describes the whole list; `hasMore` is about what follows it.
      setMeta(results[results.length - 1]?.meta ?? null)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('Failed to fetch sessions:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch sessions')
      setCanvases([])
      setMeta(null)
    } finally {
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current)
      setIsLoading(false)
    }
  }, [isAuthenticated, userId, fetchPage, firstPage])

  // Append the next page
  const loadMore = useCallback(async () => {
    if (!isAuthenticated || !userId) return
    if (!meta?.hasMore || isLoadingMoreRef.current || isLoading) return

    const nextPage = firstPage + loadedPagesRef.current

    try {
      isLoadingMoreRef.current = true
      setIsLoadingMore(true)
      setError(null)

      const controller = new AbortController()
      loadMoreAbortRef.current = controller
      const result = await fetchPage(nextPage, controller.signal)

      loadedPagesRef.current += 1
      setCanvases(prev => {
        // Guard against a page that overlaps the one before it (a session created meanwhile)
        const seen = new Set(prev.map(canvas => canvas.id))
        return [...prev, ...result.canvases.filter(canvas => !seen.has(canvas.id))]
      })
      setMeta(result.meta)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('Failed to load more sessions:', err)
      setError(err instanceof Error ? err.message : 'Failed to load more sessions')
    } finally {
      isLoadingMoreRef.current = false
      setIsLoadingMore(false)
    }
  }, [isAuthenticated, userId, meta?.hasMore, isLoading, firstPage, fetchPage])

  // Fetch trashed canvases
  const fetchTrash = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setTrashedCanvases([])
      return
    }

    try {
      setIsTrashLoading(true)
      setError(null)

      trashAbortRef.current?.abort()
      const controller = new AbortController()
      trashAbortRef.current = controller

      const response = await callApi('/api/canvas/trash', { signal: controller.signal }) as { success: boolean; data?: Canvas[]; error?: string }

      if (response.success && response.data) {
        setTrashedCanvases(response.data)
      } else {
        throw new Error(response.error || 'Failed to fetch trash')
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('Failed to fetch trash:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch trash')
      setTrashedCanvases([])
    } finally {
      setIsTrashLoading(false)
    }
  }, [isAuthenticated, userId, callApi])

  // Create new canvas
  const createCanvas = useCallback(async (input: CanvasCreateInput): Promise<Canvas | null> => {
    try {
      setError(null)

      const response = await callApi('/api/canvas', {
        method: 'POST',
        body: JSON.stringify(input),
      }) as { success: boolean; data?: Canvas; error?: string }

      if (response.success && response.data) {
        // Refresh canvas list
        await fetchCanvases()
        return response.data
      } else {
        throw new Error(response.error || 'Failed to create session')
      }
    } catch (err) {
      console.error('Failed to create session:', err)
      setError(err instanceof Error ? err.message : 'Failed to create session')
      return null
    }
  }, [callApi, fetchCanvases])

  // Update canvas
  const updateCanvas = useCallback(async (uuid: string, input: CanvasUpdateInput): Promise<Canvas | null> => {
    try {
      setError(null)

      // Store original canvas for potential revert
      let originalCanvas: Canvas | null = null

      // Optimistic update: immediately update local state
      setCanvases(prevCanvases =>
        prevCanvases.map(canvas => {
          if (canvas.uuid === uuid) {
            originalCanvas = canvas // Save original
            return { ...canvas, ...input, updatedAt: new Date().toISOString() }
          }
          return canvas
        })
      )

      const response = await callApi(`/api/canvas/${uuid}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }) as { success: boolean; data?: Canvas; error?: string }

      if (response.success && response.data) {
        // Update with server response (more precise than fetchCanvases)
        setCanvases(prevCanvases =>
          prevCanvases.map(canvas =>
            canvas.uuid === uuid
              ? response.data!
              : canvas
          )
        )
        return response.data
      } else {
        // Revert optimistic update on error
        if (originalCanvas) {
          setCanvases(prevCanvases =>
            prevCanvases.map(canvas =>
              canvas.uuid === uuid ? originalCanvas! : canvas
            )
          )
        }
        throw new Error(response.error || 'Failed to update session')
      }
    } catch (err) {
      console.error('Failed to update session:', err)
      // Revert optimistic update on error - refetch to ensure consistency
      await fetchCanvases()
      setError(err instanceof Error ? err.message : 'Failed to update session')
      return null
    }
  }, [callApi, fetchCanvases])

  // Delete canvas (soft delete by default, permanent if specified)
  const deleteCanvas = useCallback(async (uuid: string, permanent = false): Promise<boolean> => {
    try {
      setError(null)

      const queryParam = permanent ? '?permanent=true' : ''
      const response = await callApi(`/api/canvas/${uuid}${queryParam}`, {
        method: 'DELETE',
      }) as { success: boolean; error?: string }

      if (response.success) {
        // Refresh both lists
        await fetchCanvases()
        if (permanent) {
          await fetchTrash()
        }
        return true
      } else {
        throw new Error(response.error || 'Failed to delete session')
      }
    } catch (err) {
      console.error('Failed to delete session:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete session')
      return false
    }
  }, [callApi, fetchCanvases, fetchTrash])

  // Restore canvas from trash
  const restoreCanvas = useCallback(async (uuid: string): Promise<Canvas | null> => {
    try {
      setError(null)

      const response = await callApi(`/api/canvas/${uuid}/restore`, {
        method: 'PUT',
        body: JSON.stringify({}),
      }) as { success: boolean; data?: Canvas; error?: string }

      if (response.success && response.data) {
        // Refresh both lists
        await fetchCanvases()
        await fetchTrash()
        return response.data
      } else {
        throw new Error(response.error || 'Failed to restore session')
      }
    } catch (err) {
      console.error('Failed to restore session:', err)
      setError(err instanceof Error ? err.message : 'Failed to restore session')
      return null
    }
  }, [callApi, fetchCanvases, fetchTrash])

  // Duplicate canvas
  const duplicateCanvas = useCallback(async (uuid: string, title?: string): Promise<Canvas | null> => {
    try {
      setError(null)

      const response = await callApi(`/api/canvas/${uuid}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ title }),
      }) as { success: boolean; data?: Canvas; error?: string }

      if (response.success && response.data) {
        // Refresh canvas list
        await fetchCanvases()
        return response.data
      } else {
        throw new Error(response.error || 'Failed to duplicate session')
      }
    } catch (err) {
      console.error('Failed to duplicate session:', err)
      setError(err instanceof Error ? err.message : 'Failed to duplicate session')
      return null
    }
  }, [callApi, fetchCanvases])

  // Empty trash (permanently delete all trashed canvases)
  const emptyTrash = useCallback(async (): Promise<boolean> => {
    try {
      setError(null)

      const response = await callApi('/api/canvas/trash', {
        method: 'DELETE',
      }) as { success: boolean; data?: { deleted: number }; error?: string }

      if (response.success) {
        return true
      } else {
        throw new Error(response.error || 'Failed to empty trash')
      }
    } catch (err) {
      console.error('Failed to empty trash:', err)
      setError(err instanceof Error ? err.message : 'Failed to empty trash')
      return false
    } finally {
      // Always refresh both lists — even after partial failure
      try { await fetchCanvases() } catch { /* ignore refresh error */ }
      try { await fetchTrash() } catch { /* ignore refresh error */ }
    }
  }, [callApi, fetchCanvases, fetchTrash])

  // Get single canvas by UUID
  const getCanvas = useCallback(async (uuid: string): Promise<Canvas | null> => {
    try {
      setError(null)

      const response = await callApi(`/api/canvas/${uuid}`) as { success: boolean; data?: Canvas; error?: string }

      if (response.success && response.data) {
        return response.data
      } else {
        throw new Error(response.error || 'Failed to get canvas')
      }
    } catch (err) {
      console.error('Failed to get canvas:', err)
      setError(err instanceof Error ? err.message : 'Failed to get canvas')
      return null
    }
  }, [callApi])

  // A different query is a different list: start again from its first page.
  // Declared before the fetch effect so the reset lands before the fetch it belongs to.
  useEffect(() => {
    loadedPagesRef.current = 1
  }, [fetchPage, firstPage])

  // Initial fetch
  useEffect(() => {
    fetchCanvases()
    return () => {
      abortRef.current?.abort()
      trashAbortRef.current?.abort()
      loadMoreAbortRef.current?.abort()
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current)
    }
  }, [fetchCanvases])

  return {
    canvases,
    trashedCanvases,
    total: meta?.total ?? canvases.length,
    hasMore: meta?.hasMore ?? false,
    isLoading,
    isLoadingMore,
    isTrashLoading,
    error,
    refetch: fetchCanvases,
    loadMore,
    fetchTrash,
    createCanvas,
    updateCanvas,
    deleteCanvas,
    restoreCanvas,
    duplicateCanvas,
    getCanvas,
    emptyTrash,
  }
}

// Export types for use in components
export type { Canvas, CanvasCreateInput, CanvasUpdateInput, CanvasFilters } from '@/lib/api/canvas-types'
